import React, { useState, useEffect } from 'react';
import "../../css/Routine.css";
import Loader from "../../components/Loader";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaCalendarAlt, FaClock, FaChalkboardTeacher, FaUserGraduate, FaSearch, FaFileAlt, FaBook } from 'react-icons/fa';

function Routine() {
    const [classes, setClasses] = useState([]);
    const [routines, setRoutines] = useState([]);
    const [selectedClass, setSelectedClass] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [currentRoutine, setCurrentRoutine] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const weekDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

    useEffect(() => {
        fetchClasses();
        fetchAllRoutines();
    }, []);

    const fetchClasses = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch("https://schoolpro-api.uniums.com/api/classes", {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch classes");
            }

            const data = await response.json();
            setClasses(data);
        } catch (error) {
            console.error("Error fetching classes:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchAllRoutines = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch("https://schoolpro-api.uniums.com/api/routines", {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch routines");
            }

            const data = await response.json();
            setRoutines(data);
        } catch (error) {
            console.error("Error fetching routines:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClassSelect = (e) => {
        const classId = e.target.value;
        setSelectedClass(classId);

        if (classId) {
            // const routine = routines.find(r => r.classId._id === classId);
            const routine = routines.find(r => r.classId && r.classId._id === classId);
            setCurrentRoutine(routine || null);
        } else {
            setCurrentRoutine(null);
        }
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const getFilteredClasses = () => {
        if (!searchQuery) return classes;

        return classes.filter(cls =>
            cls.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            cls.section.toLowerCase().includes(searchQuery.toLowerCase())
        );
    };

    const sortPeriods = (periods) => {
        return [...periods].sort((a, b) => {
            const timeA = new Date(`2000-01-01T${a.startTime}`);
            const timeB = new Date(`2000-01-01T${b.startTime}`);
            return timeA - timeB;
        });
    };

    const getDayName = (day) => {
        const days = {
            monday: "Monday",
            tuesday: "Tuesday",
            wednesday: "Wednesday",
            thursday: "Thursday",
            friday: "Friday",
            saturday: "Saturday"
        };
        return days[day] || day;
    };

    const renderClassCards = () => {
        const filteredClasses = getFilteredClasses();

        if (filteredClasses.length === 0) {
            return (
                <div className="routine-empty-state">
                    <FaFileAlt className="routine-empty-icon" />
                    <p>No classes found matching your search.</p>
                </div>
            );
        }

        return filteredClasses.map(cls => {
            const routine = routines.find(r => r.classId && r.classId._id === cls._id);
            const totalPeriods = routine ? Object.values(routine.schedule).reduce(
                (sum, daySchedule) => sum + daySchedule.length, 0
            ) : 0;

            return (
                <div
                    key={cls._id}
                    className="routine-class-card"
                    onClick={() => {
                        setSelectedClass(cls._id);
                        setCurrentRoutine(routine || null);
                    }}
                >
                    <div className="routine-class-card-header">
                        <FaBook className="routine-class-icon" />
                        <h3>{cls.name} - {cls.section}</h3>
                    </div>
                    <div className="routine-class-card-body">
                        <div className="routine-class-stat">
                            <span className="routine-stat-label">Total Periods:</span>
                            <span className="routine-stat-value">{totalPeriods}</span>
                        </div>
                        <div className="routine-class-stat">
                            <span className="routine-stat-label">Days:</span>
                            <span className="routine-stat-value">
                                {routine ? weekDays.filter(day => routine.schedule[day].length > 0).length : 0}
                            </span>
                        </div>
                    </div>
                    <div className="routine-class-card-footer">
                        <button className="routine-view-btn">
                            View Routine
                        </button>
                    </div>
                </div>
            );
        });
    };

    const renderRoutineSchedule = () => {
        if (!currentRoutine) return null;

        return (
            <div className="routine-schedule">
                <div className="routine-schedule-header">
                    <h2>
                        <FaChalkboardTeacher className="routine-header-icon" />
                        Class Routine: {currentRoutine.classId.name} - {currentRoutine.classId.section}
                    </h2>
                </div>

                <div className="routine-days-container">
                    {weekDays.map(day => {
                        const periods = currentRoutine.schedule[day];
                        const sortedPeriods = sortPeriods(periods);

                        return (
                            <div key={day} className="routine-day-card">
                                <div className="routine-day-header">
                                    <h3>{getDayName(day)}</h3>
                                    <span className="routine-period-count">
                                        {periods.length} {periods.length === 1 ? 'period' : 'periods'}
                                    </span>
                                </div>

                                <div className="routine-periods-list">
                                    {sortedPeriods.length === 0 ? (
                                        <div className="routine-no-periods">
                                            <p>No classes scheduled</p>
                                        </div>
                                    ) : (
                                        sortedPeriods.map((period, index) => (
                                            <div key={index} className="routine-period-item">
                                                <div className="routine-period-time">
                                                    <FaClock className="routine-time-icon" />
                                                    <span>{period.startTime} - {period.endTime}</span>
                                                </div>
                                                <div className="routine-period-info">
                                                    <h4 className="routine-subject">{period.subject}</h4>
                                                    <p className="routine-teacher">
                                                        <FaUserGraduate className="routine-teacher-icon" />
                                                        {period.teacher}
                                                    </p>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div className="routine-container">
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />

            {isLoading && <Loader />}

            <div className="routine-header">
                <div className="routine-title-container">
                    <FaCalendarAlt className="routine-title-icon" />
                    <h1 className="routine-title">Class Routines</h1>
                </div>

                <div className="routine-search-container">
                    <div className="routine-search-input-wrapper">
                        <FaSearch className="routine-search-icon" />
                        <input
                            type="text"
                            className="routine-search-input"
                            placeholder="Search by class name or section..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                        />
                    </div>

                    <select
                        className="routine-select"
                        value={selectedClass}
                        onChange={handleClassSelect}
                    >
                        <option value="">Select a Class</option>
                        {classes.map(cls => (
                            <option key={cls._id} value={cls._id}>
                                {cls.name} - {cls.section}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {!currentRoutine ? (
                <div className="routine-classes-grid">
                    {renderClassCards()}
                </div>
            ) : (
                renderRoutineSchedule()
            )}

            {currentRoutine && (
                <button
                    className="routine-back-btn"
                    onClick={() => {
                        setSelectedClass('');
                        setCurrentRoutine(null);
                    }}
                >
                    &larr; Back to All Classes
                </button>
            )}
        </div>
    );
}

export default Routine;